import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  getNextUrlQuery,
  getSearchParams,
} from '@/common/helpers/URLQueryHelpers';

/**
 * @param {Object.<string, *>} params
 * @param {Object.<string, {name: string, defaultValue: *, encode: function(*): string|null, decode: function(string|null): *}>} paramsConfig
 * @return {Object.<string, string>}
 */
const encodeParams = (params, paramsConfig) =>
  Object.keys(params).reduce((accu, paramName) => {
    if (paramsConfig[paramName] === undefined) {
      return accu;
    }
    return {
      ...accu,
      [paramName]: paramsConfig[paramName].encode(params[paramName]),
    };
  }, {});

/**
 * @param {Object.<string, string>} searchParams
 * @param {Object.<string, {name: string, defaultValue: *, encode: function(*): string|null, decode: function(string|null): *}>} paramsConfig
 * @returns string
 */
const getSearchParamsSignature = (searchParams, paramsConfig) =>
  Object.keys(searchParams).reduce(
    (accu, paramName) =>
      paramsConfig[paramName]
        ? `${accu}-${
            searchParams[paramName] ? searchParams[paramName].toString() : ''
          }`
        : accu,
    '',
  );

/**
 * @param {Object.<string, (string|string[])>} searchParams
 * @param {Object.<string, {name: string, defaultValue: *, encode: function(*): string|null, decode: function(string|null): *}>} paramsConfig
 * @returns {Object.<string, *>}
 */
const getInitialParams = (searchParams, paramsConfig) => {
  const initialParams = {};
  Object.entries(paramsConfig).forEach(
    ([paramName, { defaultValue, decode }]) => {
      const searchParam = searchParams[paramName];
      initialParams[paramName] = searchParam
        ? decode(searchParam)
        : defaultValue;
    },
  );

  return initialParams;
};

/**
 * @param {Object.<string, *>} params
 * @param {Object.<string, {name: string, defaultValue: *, encode: function(*): string|null, decode: function(string|null): *}>} paramsConfig
 * @returns {Object.<string, *>}
 */
const getApiParams = (params, paramsConfig) => {
  const apiParams = {};

  Object.entries(paramsConfig).forEach(([paramName, { encode }]) => {
    if (params[paramName] && params[paramName] !== null) {
      apiParams[paramName] = encode(params[paramName]);
    }
  });

  return apiParams;
};

/**
 * Manage data parameters
 * Keep sync between url query and params state
 * @param {Object.<string, {name: string, defaultValue: *, encode: function(*): string|null, decode: function(string|null): *}>} paramsConfig
 *   name: url query and state name
 *   defaultValue: default value on state initialization
 *   encode: state to url query
 *   decode : url query to state
 * @param {function(nextParams: Object.<string, *>, prevParams:Object.<string, *> = undefined): Object.<string, *>} consistencyEncoder
 *    Fix wrong parameters associations
 * @param {function(params: Object.<string, *>): Object.<string, *>} apiParamsEncoder
 *    Allows modifications on parameters sent to the API
 *
 * @return {{hasSearchParamsDiff: boolean, params: unknown, getParams: ((function((string[]|string)): (Object<string, *>|*))|*), setParams, apiParams: Object<string, *>}}
 */
const usePageParams = (
  paramsConfig,
  consistencyEncoder = (nextParams) => nextParams,
  apiParamsEncoder = (params) => params,
) => {
  const history = useHistory();
  const location = useLocation();
  const URLSearchParamsInstance = new URLSearchParams(location.search);
  const searchParams = getSearchParams(
    Object.keys(paramsConfig),
    URLSearchParamsInstance,
  );
  const searchParamsSignature = getSearchParamsSignature(
    searchParams,
    paramsConfig,
  );
  const prevSearchParamsSignatureRef = useRef(searchParamsSignature);

  const [params, setParams] = useState(
    consistencyEncoder(getInitialParams(searchParams, paramsConfig)),
  );

  useEffect(() => {
    if (prevSearchParamsSignatureRef.current !== searchParamsSignature) {
      setParams(
        consistencyEncoder(getInitialParams(searchParams, paramsConfig)),
      );
    }
    prevSearchParamsSignatureRef.current = searchParamsSignature;
  }, [
    searchParamsSignature,
    prevSearchParamsSignatureRef,
    searchParams,
    paramsConfig,
    consistencyEncoder,
  ]);

  /**
   * @param {string[]|string} paramName
   * @returns {Object.<string, *> | *}
   */
  const getParams = (paramName) => {
    if (Array.isArray(paramName)) {
      return paramName.reduce(
        (accu, oneParamName) =>
          paramsConfig[oneParamName] && params[oneParamName]
            ? {
                ...accu,
                [oneParamName]: params[oneParamName],
              }
            : accu,
        {},
      );
    }

    return params[paramName];
  };

  /**
   * @param {Object.<string, *>} nextValues
   * @param {string|null} nextPathname
   * @param {boolean} replaceUrl
   */
  const setParamsHandler = (
    nextValues,
    nextPathname = null,
    replaceUrl = false,
  ) => {
    const nextUrlQuery = {};
    let nextParams = { ...params };
    Object.keys(nextValues).forEach((paramName) => {
      if (paramsConfig[paramName]) {
        nextParams[paramName] = nextValues[paramName];
      }
    });
    nextParams = consistencyEncoder(nextParams, params);
    Object.keys(nextParams).forEach((paramName) => {
      if (paramsConfig[paramName]) {
        nextUrlQuery[paramName] = paramsConfig[paramName].encode(
          nextParams[paramName],
        );
      }
    });
    prevSearchParamsSignatureRef.current = getSearchParamsSignature(
      encodeParams(nextParams, paramsConfig),
      paramsConfig,
    );
    const navigationMethod = replaceUrl ? history.replace : history.push;
    navigationMethod({
      pathname: nextPathname || location.pathname,
      search: getNextUrlQuery(nextUrlQuery, URLSearchParamsInstance),
    });
    setParams(nextParams);
  };

  return {
    params,
    getParams,
    setParams: setParamsHandler,
    apiParams: getApiParams(apiParamsEncoder(params), paramsConfig),
    hasSearchParamsDiff:
      prevSearchParamsSignatureRef.current !== searchParamsSignature,
  };
};

export default usePageParams;
